/**
 * Où en est-on avec les assureurs — chunk 9.AG.1.
 *
 * Répond à la question que le conseiller pose en ouvrant un dossier en cours :
 * combien d'assureurs ont répondu, lesquels se taisent encore, et depuis combien
 * de temps.
 *
 * ## Consigner une réponse depuis ici
 *
 * Le geste vit sur la LIGNE de l'assureur concerné, pas dans un formulaire séparé :
 * c'est en regardant « Beneva — en attente depuis 20 jours » qu'on se souvient
 * qu'ils ont répondu par téléphone hier.
 *
 * Sans ce geste, le métier de rappels relancerait un assureur qui a déjà répondu —
 * c'est la raison d'être du chunk, pas un confort.
 *
 * ## Rien n'est recalculé
 *
 * Les compteurs, les statuts et les délais arrivent du serveur. Les recalculer
 * installerait une seconde définition de « a répondu ».
 */
import type { QuoteRequestRecipientStatus } from "@kollectivia/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import {
  useInsurerLoop,
  useRecordInsurerResponse,
  type InsurerLoopRecipient,
  type InsurerLoopRequest,
} from "../../../lib/queries/deadlines";
import { useInsurerCatalog } from "../../../lib/queries/insurers";
import { useCreateQuoteRequest } from "../../../lib/queries/quoteRequests";

import { RecipientStatusBadge } from "./RecipientStatusBadge";

/**
 * Le vert dit « on a une réponse », l'ardoise « on attend », l'ambre « ils ont
 * décliné ». Décliner n'est pas un échec — c'est une réponse, et la traiter en
 * rouge ferait paniquer pour un dossier qui avance.
 */
const ATTENTE = new Set(["pending", "sent", "viewed"]);

function Destinataire({
  item,
  quoteRequestId,
  projectId,
  figee,
}: {
  readonly item: InsurerLoopRecipient;
  readonly quoteRequestId: string;
  readonly projectId: string;
  readonly figee: boolean;
}) {
  const { t } = useTranslation();
  const insurers = useInsurerCatalog();
  const [ouvert, setOuvert] = useState(false);
  const enregistrer = useRecordInsurerResponse(projectId);
  const attendu = ATTENTE.has(item.status);

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-100 py-2.5 first:border-t-0">
      <RecipientStatusBadge status={item.status as QuoteRequestRecipientStatus} />
      <span className="min-w-0 flex-1 text-sm text-slate-900" translate="no">
        {insurers.labelFor(item.insurer_code)}
      </span>
      {item.response_days !== null && (
        <span className="text-xs text-slate-500">
          {t("insurer_loop.answered_in", { count: item.response_days })}
        </span>
      )}

      {attendu && !figee && (
        <span className="inline-flex flex-wrap gap-2">
          {ouvert ? (
            <>
              <button
                type="button"
                disabled={enregistrer.isPending}
                onClick={() =>
                  enregistrer.mutate(
                    { quoteRequestId, recipientId: item.recipient_id, kind: "respond" },
                    { onSettled: () => setOuvert(false) },
                  )
                }
                className="focus-visible:ring-brand-500 rounded-lg border border-emerald-300 px-2.5 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50"
              >
                {t("insurer_loop.action.responded")}
              </button>
              <button
                type="button"
                disabled={enregistrer.isPending}
                onClick={() =>
                  enregistrer.mutate(
                    { quoteRequestId, recipientId: item.recipient_id, kind: "decline" },
                    { onSettled: () => setOuvert(false) },
                  )
                }
                className="focus-visible:ring-brand-500 rounded-lg border border-amber-300 px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50"
              >
                {t("insurer_loop.action.declined")}
              </button>
              <button
                type="button"
                onClick={() => setOuvert(false)}
                className="focus-visible:ring-brand-500 rounded-lg px-2 py-1 text-xs text-slate-500 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
              >
                {t("common.cancel")}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setOuvert(true)}
              className="focus-visible:ring-brand-500 rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
            >
              {t("insurer_loop.action.record")}
            </button>
          )}
        </span>
      )}

      {enregistrer.isError && (
        <span className="w-full text-xs text-red-800" role="alert">
          {t("insurer_loop.record_error")}
        </span>
      )}
    </li>
  );
}

function Demande({
  demande,
  projectId,
}: {
  readonly demande: InsurerLoopRequest;
  readonly projectId: string;
}) {
  const { t } = useTranslation();
  // Une demande annulée ou expirée n'accepte plus de réponse côté serveur. Proposer
  // le geste ici mènerait à un refus qu'on aurait pu éviter d'afficher.
  const figee = demande.status === "cancelled" || demande.status === "expired";

  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium text-slate-900">
          {demande.title ?? t("insurer_loop.untitled")}
        </h3>
        <div className="flex items-baseline gap-3">
          <span className="text-xs text-slate-500">
            {t(`insurer_loop.request.${demande.status}`, {
              defaultValue: demande.status,
            })}
          </span>
          {/* 9.AZ.1 — la porte du parcours d'envoi. Sur un brouillon elle invite à
              préparer ; ensuite elle mène au suivi, qui vit au même endroit. */}
          <Link
            to={`/quote-requests/${demande.quote_request_id}/preparer`}
            className="focus-visible:ring-brand-500 text-brand-700 hover:text-brand-800 text-xs font-medium underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            {demande.status === "draft"
              ? t("insurer_loop.prepare_and_send")
              : t("insurer_loop.open_tracking")}
          </Link>
        </div>
      </div>

      <p className="mt-1 text-sm text-slate-600">
        {t("insurer_loop.progress", {
          answered: demande.answered,
          total: demande.total,
        })}
        {demande.declined > 0 && (
          <span className="text-amber-800">
            {" · "}
            {t("insurer_loop.declined_count", { count: demande.declined })}
          </span>
        )}
      </p>

      {demande.recipients.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">{t("insurer_loop.no_recipients")}</p>
      ) : (
        <ul className="mt-2">
          {demande.recipients.map((r) => (
            <Destinataire
              key={r.recipient_id}
              item={r}
              quoteRequestId={demande.quote_request_id}
              projectId={projectId}
              figee={figee}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

export function InsurerLoopSection({ projectId }: { readonly projectId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const req = useInsurerLoop(projectId);
  const creerDemande = useCreateQuoteRequest();

  /**
   * 9.BA.1 — la porte du parcours d'envoi depuis un dossier existant.
   *
   * Le cas 2 du raccord : celui qui est entré par « Projets → Nouveau projet »
   * n'a pas déclaré d'intention d'envoi, donc rien n'a été créé pour lui. Ce
   * bouton est le geste explicite qui la déclare — et il mène directement à
   * l'écran où l'on choisit le cahier de charges, pas à un dialogue qui
   * redemanderait les assureurs avant.
   */
  async function preparerUneDemande() {
    if (creerDemande.isPending) return;
    try {
      const demande = await creerDemande.mutateAsync({ input: { project_id: projectId } });
      navigate(`/quote-requests/${demande.id}/preparer`);
    } catch {
      // L'erreur est rendue sous le bouton — le dossier, lui, reste intact.
    }
  }

  if (req.isPending) {
    return (
      <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="text-sm text-slate-500" aria-live="polite">
          {t("insurer_loop.loading")}
        </p>
      </section>
    );
  }

  if (req.isError || !req.data || !Array.isArray(req.data.requests)) {
    return (
      <section className="mt-6 rounded-xl border border-red-200 bg-red-50 p-5 shadow-sm">
        <p className="text-sm text-red-800" role="alert">
          {t("insurer_loop.error")}
        </p>
      </section>
    );
  }

  const demandes = req.data.requests.filter((d) => d?.quote_request_id);

  return (
    <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-slate-900">{t("insurer_loop.title")}</h2>
        <div className="flex flex-wrap items-baseline gap-3">
          {demandes.length > 0 && (
            <p className="text-sm text-slate-600">
              {t("insurer_loop.summary", {
                answered: req.data.answered,
                awaiting: req.data.awaiting,
              })}
            </p>
          )}
          <button
            type="button"
            disabled={creerDemande.isPending}
            aria-busy={creerDemande.isPending}
            onClick={() => void preparerUneDemande()}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 touch-manipulation rounded-lg px-3 py-1.5 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {creerDemande.isPending ? t("app.loading") : t("insurer_loop.prepare_new_request")}
          </button>
        </div>
      </div>
      {creerDemande.isError && (
        <p role="alert" className="mt-2 text-sm text-red-800">
          {t("insurer_loop.prepare_new_request_error")}
        </p>
      )}

      {demandes.length === 0 ? (
        <p className="mt-4 rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-600">
          {t("insurer_loop.empty")}
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          {demandes.map((d) => (
            <Demande key={d.quote_request_id} demande={d} projectId={projectId} />
          ))}
        </div>
      )}
    </section>
  );
}
